import type { ParqAnswers } from "@repo/contract/training";
import {
  "intake.no" as noText,
  "onboarding.parq.1" as parq1,
  "onboarding.parq.2" as parq2,
  "onboarding.parq.3" as parq3,
  "onboarding.parq.4" as parq4,
  "onboarding.parq.5" as parq5,
  "onboarding.parq.6" as parq6,
  "onboarding.parq.7" as parq7,
  "intake.why.parq.long" as parqWhy,
  "profile-editors.parq_positive" as positiveText,
  "intake.yes" as yesText,
} from "@repo/i18n";
import { useCallback } from "react";
import { Segmented } from "../../../ui/controls";
import { Helper } from "./parts";

/**
 * The seven PAR-Q+ questions, in the questionnaire's order — the order IS the
 * identity on the wire (parq.ts), so the questions are the intake's own
 * (`onboarding.parq.1…7`), not A2's reworded seven.
 */
const QUESTIONS = [parq1, parq2, parq3, parq4, parq5, parq6, parq7] as const;

export const NO_PARQ: ParqAnswers = [
  false,
  false,
  false,
  false,
  false,
  false,
  false,
];

export interface ParqFormProps {
  onChange: (value: ParqAnswers) => void;
  value: ParqAnswers;
}

/** A2 `ParqSheet` (editors.tsx:794-848). */
export function ParqForm({ value, onChange }: ParqFormProps) {
  const positives = value.filter(Boolean).length;
  return (
    <div>
      <Helper>{parqWhy()}</Helper>
      <ol className="flex flex-col gap-3">
        {QUESTIONS.map((question, index) => (
          <ParqQuestion
            answers={value}
            index={index}
            key={question()}
            onChange={onChange}
            question={question()}
          />
        ))}
      </ol>
      {positives > 0 ? (
        <p className="mt-4 rounded-lg bg-accent-soft p-4 text-base text-ink leading-snug">
          {positiveText({ count: positives })}
        </p>
      ) : null}
    </div>
  );
}

type YesNo = "no" | "yes";

function ParqQuestion({
  answers,
  index,
  question,
  onChange,
}: {
  answers: ParqAnswers;
  index: number;
  onChange: (value: ParqAnswers) => void;
  question: string;
}) {
  const change = useCallback(
    (answer: YesNo) =>
      onChange(
        answers.map((a, i) =>
          i === index ? answer === "yes" : a
        ) as ParqAnswers
      ),
    [answers, index, onChange]
  );
  return (
    <li className="rounded-2xl bg-surface p-4 shadow">
      <p className="mb-3 font-medium text-base leading-snug">
        <span className="text-ink-3 tabular-nums">{index + 1}. </span>
        {question}
      </p>
      <Segmented<YesNo>
        label={question}
        onValueChange={change}
        options={[
          { label: noText(), value: "no" },
          { label: yesText(), value: "yes" },
        ]}
        value={answers[index] ? "yes" : "no"}
      />
    </li>
  );
}
